import React, { useState, useEffect, useRef } from "react";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useAuth } from "../../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import {
  FaHome,
  FaChevronLeft,
  FaComments,
  FaEdit,
  FaTrashAlt,
  FaBars,
  FaTimes,
  FaPlus,
  FaRobot,
  FaPaperPlane,
  FaSync,
} from "react-icons/fa";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

type Message = {
  id: string;
  role: "user" | "assistant";
  content: string;
};

export default function ChatApp() {
  const { student } = useAuth();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState<any[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [confirmDeleteSession, setConfirmDeleteSession] = useState<
    string | null
  >(null);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editedTitle, setEditedTitle] = useState<string>("");
  const [refreshing, setRefreshing] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const sseController = useRef<AbortController | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    fetchSessions();
  }, []);

  async function fetchSessions() {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_BASE}/api/chat/sessions`, {
        credentials: "include",
      });
      const data = await res.json();
      setSessions(data.sessions || []);
      if (data.sessions?.length && !currentSessionId) {
        selectSession(String(data.sessions[0].id));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setRefreshing(false);
    }
  }

  async function selectSession(id: string) {
    setCurrentSessionId(id);
    setEditingSessionId(null);
    setMobileSidebarOpen(false);
    try {
      const res = await fetch(`${API_BASE}/api/chat/sessions/${id}/messages`, {
        credentials: "include",
      });
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (e) {
      console.error(e);
    }
  }

  async function createSession() {
    if (streaming) return;
    try {
      const res = await fetch(`${API_BASE}/api/chat/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const data = await res.json();
      const newId = String(data.sessionId ?? data.id);
      await fetchSessions();
      if (newId) selectSession(newId);
      setEditingSessionId(newId);
      setEditedTitle("New Chat");
    } catch (e) {
      console.error(e);
    }
  }

  async function deleteSession(id: string | null) {
    if (streaming || !id) return;
    setConfirmDeleteSession(null);
    try {
      await fetch(`${API_BASE}/api/chat/sessions/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      setSessions((prev) => prev.filter((s) => String(s.id) !== String(id)));
      if (id === currentSessionId) {
        setCurrentSessionId(null);
        setMessages([]);
      }
      if (id === editingSessionId) {
        setEditingSessionId(null);
        setEditedTitle("");
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function updateSessionTitle(id: string, title: string) {
    try {
      const r = await fetch(`${API_BASE}/api/chat/sessions/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
        credentials: "include",
      });
      if (!r.ok) {
        await fetch(`${API_BASE}/api/chat/sessions/${id}/title`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title }),
          credentials: "include",
        });
      }
      setSessions((prev) =>
        prev.map((s) => (String(s.id) === String(id) ? { ...s, title } : s))
      );
      setEditingSessionId(null);
      setEditedTitle("");
    } catch (e) {
      console.error("Failed to update session title", e);
    }
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInput(e.target.value);
  }

  async function sendMessage() {
    if (!input.trim() || !currentSessionId || streaming) return;

    const userMessage = input.trim();
    const userMessageId = `u-${Date.now()}`;
    setInput("");

    setMessages((msgs) => [
      ...msgs,
      { id: userMessageId, role: "user", content: userMessage },
    ]);
    setStreaming(true);

    sseController.current?.abort();
    const controller = new AbortController();
    sseController.current = controller;

    let assistantContent = "";
    let assistantMessageId = `a-${Date.now()}`;

    try {
      await fetchEventSource(
        `${API_BASE}/api/chat/sessions/${currentSessionId}/messages/stream`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content: userMessage }),
          signal: controller.signal,
          onmessage(event) {
            const data = event.data;
            if (!data || data === "ready" || data === "ok") return;
            assistantContent += data;

            setMessages((msgs) => {
              const lastMsg = msgs[msgs.length - 1];
              if (
                lastMsg &&
                lastMsg.role === "assistant" &&
                lastMsg.id === assistantMessageId
              ) {
                return [
                  ...msgs.slice(0, -1),
                  { ...lastMsg, content: assistantContent },
                ];
              } else {
                return [
                  ...msgs,
                  {
                    id: assistantMessageId,
                    role: "assistant",
                    content: assistantContent,
                  },
                ];
              }
            });
          },
          onclose() {
            setStreaming(false);
          },
          onerror(err) {
            console.error("SSE error:", err);
            setStreaming(false);
            controller.abort();
          },
          openWhenHidden: true,
        }
      );
    } catch (error) {
      console.error("Send message error:", error);
      setStreaming(false);
    }
  }

  function onFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    sendMessage();
  }

  function startDeleteSession(id: string) {
    setConfirmDeleteSession(id);
  }
  function cancelDeleteSession() {
    setConfirmDeleteSession(null);
  }

  function renderSessionTitle(session: any) {
    if (editingSessionId === String(session.id)) {
      return (
        <input
          autoFocus
          className="bg-transparent border-b border-gray-300 px-1 py-0.5 w-full text-sm font-medium focus:outline-none text-gray-800 placeholder-gray-500 focus:border-[#009fe3]"
          style={{ fontSize: "16px" }}
          type="text"
          value={editedTitle}
          onChange={(e) => setEditedTitle(e.target.value)}
          onBlur={() => {
            const title = editedTitle.trim() || session.title || "New Chat";
            if (title !== session.title)
              updateSessionTitle(String(session.id), title);
            else {
              setEditingSessionId(null);
              setEditedTitle("");
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            if (e.key === "Escape") {
              setEditingSessionId(null);
              setEditedTitle("");
            }
          }}
        />
      );
    }
    return (
      <span
        className="text-sm font-medium truncate cursor-pointer hover:text-[#009fe3] transition-colors"
        onDoubleClick={() => {
          setEditingSessionId(String(session.id));
          setEditedTitle(session.title || "New Chat");
        }}
        title="Double-click to edit"
      >
        {session.title || `Chat ${session.id}`}
      </span>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header - Keep Original White Style */}
      <header className="bg-white shadow-sm border-b border-gray-200 z-50">
        <div className="px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between mb-2 sm:mb-0">
            <div className="flex items-center gap-3 sm:gap-4">
              <button
                onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
              >
                {mobileSidebarOpen ? <FaTimes /> : <FaBars />}
              </button>

              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#009fe3] to-cyan-500 rounded-xl flex items-center justify-center shadow-md">
                  <FaRobot className="text-white text-lg sm:text-xl" />
                </div>
                <div>
                  <h1 className="text-base sm:text-xl font-bold text-gray-900">
                    Study Assistant
                  </h1>
                  <div className="text-xs text-gray-500 font-medium hidden sm:block">
                    Guideline2Germany
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Breadcrumb - Always visible */}
          <nav className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 mt-2 sm:mt-3">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-gray-900 transition-colors"
            >
              <FaHome className="text-xs" />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <span>/</span>
            <button
              onClick={() => navigate("/student/student-dashboard")}
              className="flex items-center gap-1 hover:text-gray-900 transition-colors"
            >
              <FaChevronLeft className="text-xs" />
              <span className="hidden sm:inline">Dashboard</span>
            </button>
            <span>/</span>
            <span className="text-gray-900 font-medium">Study Assistant</span>
          </nav>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden relative">
        {/* Mobile Backdrop */}
        {mobileSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/30 z-40 lg:hidden backdrop-blur-sm"
            style={{ top: "100px" }}
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`${
            mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0 fixed lg:static left-0 z-50 w-80 bg-white border-r border-gray-200 transition-transform duration-300 ease-in-out flex flex-col lg:h-full`}
          style={{
            top: mobileSidebarOpen ? "100px" : undefined,
            height: mobileSidebarOpen ? "calc(100vh - 100px)" : "100%",
          }}
        >
          {/* Sidebar Header */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                <FaComments className="text-[#009fe3]" />
                Chat Sessions
              </h2>
              <button
                onClick={createSession}
                disabled={streaming}
                className="flex items-center gap-2 px-3 py-2 bg-[#009fe3] text-white text-sm font-semibold rounded-lg hover:bg-[#007cb5] disabled:opacity-50 transition-all shadow-sm"
              >
                <FaPlus className="text-xs" />
                New
              </button>
            </div>
            {student?.firstName && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-gray-700 text-sm">
                  Welcome, {student.firstName}! Ask me anything about your
                  studies.
                </p>
              </div>
            )}
          </div>

          {/* Sessions List */}
          <div className="flex-1 overflow-y-auto p-3">
            {sessions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <div className="bg-gray-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border border-gray-100">
                  <FaComments className="text-2xl text-gray-400" />
                </div>
                <p className="text-sm font-medium">No chats yet</p>
                <p className="text-xs text-gray-400 mt-1">
                  Create your first chat to get started
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    onClick={() => selectSession(String(session.id))}
                    className={`group flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                      String(session.id) === String(currentSessionId)
                        ? "bg-blue-50 border border-blue-200 shadow-sm"
                        : "hover:bg-gray-50 border border-transparent"
                    }`}
                  >
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaComments className="text-gray-500 text-sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {renderSessionTitle(session)}
                      <div className="text-xs text-gray-400 mt-0.5">
                        {new Date(
                          session.updatedAt || session.createdAt
                        ).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSessionId(String(session.id));
                          setEditedTitle(session.title || "New Chat");
                        }}
                        className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
                        title="Edit title"
                      >
                        <FaEdit className="text-xs text-gray-600" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDeleteSession(String(session.id));
                        }}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                        title="Delete chat"
                      >
                        <FaTrashAlt className="text-xs" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* Main Chat Area */}
        <main className="flex-1 flex flex-col min-w-0 bg-white">
          {!currentSessionId ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <div className="w-20 h-20 bg-gradient-to-br from-[#009fe3] to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <FaRobot className="text-3xl text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Start a conversation
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Ask anything about German language or your coursework. Your
                  chats will be automatically deleted after 30 days.
                </p>
                <button
                  onClick={createSession}
                  className="px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  Create New Chat
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gradient-to-b from-gray-50 to-white">
                {messages.map(({ id, role, content }) => (
                  <div
                    key={id}
                    className={`flex ${
                      role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`flex items-end gap-3 max-w-[85%] sm:max-w-[75%] md:max-w-[65%] ${
                        role === "user" ? "flex-row-reverse" : "flex-row"
                      }`}
                    >
                      {/* Avatar */}
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          role === "user"
                            ? "bg-gradient-to-br from-[#009fe3] to-cyan-500"
                            : "bg-gradient-to-br from-gray-100 to-gray-200"
                        }`}
                      >
                        {role === "user" ? (
                          <span className="text-white text-sm font-bold">
                            {student?.firstName?.[0] || "U"}
                          </span>
                        ) : (
                          <FaRobot className="text-gray-600 text-sm" />
                        )}
                      </div>

                      {/* Message */}
                      <div
                        className={`px-4 py-3 rounded-2xl whitespace-pre-wrap shadow-sm ${
                          role === "user"
                            ? "bg-gradient-to-br from-[#009fe3] to-cyan-500 text-white"
                            : "bg-white border border-gray-200 text-gray-900"
                        }`}
                      >
                        {content}
                      </div>
                    </div>
                  </div>
                ))}
                {streaming &&
                  messages[messages.length - 1]?.role === "user" && (
                    <div className="flex justify-start">
                      <div className="flex items-end gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
                          <FaRobot className="text-gray-600 text-sm" />
                        </div>
                        <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl shadow-sm">
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-[#009fe3] rounded-full animate-pulse"></div>
                            <div
                              className="w-2 h-2 bg-[#009fe3] rounded-full animate-pulse"
                              style={{ animationDelay: "0.2s" }}
                            ></div>
                            <div
                              className="w-2 h-2 bg-[#009fe3] rounded-full animate-pulse"
                              style={{ animationDelay: "0.4s" }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form
                onSubmit={onFormSubmit}
                className="border-t border-gray-200 bg-white p-6"
              >
                <div className="flex gap-3 max-w-4xl mx-auto">
                  <input
                    type="text"
                    value={input}
                    onChange={onInputChange}
                    disabled={streaming}
                    placeholder={
                      streaming
                        ? "Thinking..."
                        : "Type your message... (Bangla / Deutsch / English)"
                    }
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all shadow-sm"
                    style={{ fontSize: "16px" }}
                  />
                  <button
                    type="submit"
                    disabled={!input.trim() || streaming}
                    className="px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:-translate-y-0.5 disabled:transform-none flex items-center gap-2"
                  >
                    <FaPaperPlane className="text-sm" />
                    Send
                  </button>
                </div>
              </form>
            </>
          )}
        </main>
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDeleteSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaTrashAlt className="text-red-600 text-lg" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Delete Chat
              </h3>
              <p className="text-gray-600 text-sm">
                Are you sure you want to delete this chat? This action cannot be
                undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => deleteSession(confirmDeleteSession)}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={cancelDeleteSession}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-900 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
